import { SvgClose } from '@/assets/ic_close';
import Loading from '@/components/loading/skeleton';
import { percentage } from '@/components/trading-panel/hooks/useGetPosition';
import { TradeData } from '@/types/trade';
import { formatDisplay } from '@/utils/format-display';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Sheet } from 'react-modal-sheet';
import FeesCard from './FeesCard';
import { useAddress } from '@/store/selector';
import { useGetFees } from './useGetFees';
import { useMemo } from 'react';

import BigNumber from 'bignumber.js';
type Props = {
    item: TradeData;
    isOpen: boolean;
    onClose: () => void;
};

export default function SheetDetail({ item, isOpen, onClose }: Props) {
    const { t } = useTranslation();

    const address = useAddress();

    const { data } = useGetFees(
        address,
        item.indexPrice,
        item.index,
        item.createdAt,
        item.buy,
        item.initialPosToken,
        item.leverage
    );

    const earn = useMemo(() => {
        const fundingFee = new BigNumber(data?.fundingFee || 0);
        const rolloverFee = new BigNumber(data?.rolloverFee || 0);
        const netEarn = new BigNumber(item.net_pnl?.earn || 0);

        return netEarn.minus(fundingFee).minus(rolloverFee).toNumber();
    }, [data?.fundingFee, data?.rolloverFee, item.net_pnl?.earn]);

    const pnl = useMemo(() => {
        const collateral = new BigNumber(item.collateralValue || 0);
        if (collateral.isZero()) return 0; // tránh chia cho 0

        const value = new BigNumber(earn).div(collateral).multipliedBy(100);

        console.debug('collateral-------', value.toString());

        // Giới hạn nhỏ nhất là -100
        const minValue = new BigNumber(-100);
        const finalValue = BigNumber.maximum(value, minValue);

        return finalValue.toNumber();
    }, [earn, item.collateralValue]);

    return (
        <Sheet isOpen={isOpen} onClose={onClose} detent="content-height">
            <Sheet.Backdrop className="!bg-transparent3" onTap={onClose} />

            <Sheet.Container className="!bg-transparent p-4  mb-2 flex !flex-col justify-end">
                <Sheet.Content className="h-fit !bg-transparent flex !flex-col justify-end z-50">
                    <div className="flex flex-col  !bg-[#141414] mt-auto p-6 h-fit !rounded-4xl">
                        <div className="flex justify-between items-center ">
                            <img
                                src={item.imgUrl}
                                alt={item.pairName}
                                className="w-6 h-6 rounded-full"
                            />
                            <button onClick={onClose}>
                                <SvgClose />
                            </button>
                        </div>
                        <div className="flex justify-between items-center mt-3">
                            <p className="text-white text-2xl">
                                {item.pairName}
                            </p>
                            <div className="flex items-center gap-x-2">
                                <p
                                    className={clsx(
                                        'uppercase text-[11px] leading-4 border rounded-md ml-2 px-1',
                                        {
                                            'text-green1 border-green1':
                                                !!item.buy,
                                            '!text-red1 !border-red1':
                                                !item.buy,
                                        }
                                    )}
                                >
                                    {item.type}
                                </p>

                                <p className="uppercase text-[11px] leading-4 text-white border-white border rounded-md px-1.5">
                                    {item.leverage}x
                                </p>
                            </div>
                        </div>

                        <div className="mt-6 flex flex-col gap-y-5">
                            <div className="flex justify-between items-center ">
                                <p className="text-[15px] text-[#4C4C4C]">
                                    {t('Use')}
                                </p>

                                <p className="text-[15px] text-white">
                                    {formatDisplay(item.collateralValue, {
                                        decimalToShow: 2,
                                    })}{' '}
                                    {item.tradeCoinName}
                                </p>
                            </div>

                            <div className="flex justify-between items-center ">
                                <p className="text-[15px] text-[#4C4C4C]">
                                    {t('Position')}
                                </p>

                                <p className="text-[15px] text-white">
                                    {formatDisplay(item.sizeValue, {
                                        decimalToShow: 2,
                                    })}{' '}
                                    {item.tradeCoinName}
                                </p>
                            </div>

                            <div className="flex justify-between items-center ">
                                <p className="text-[15px] text-[#4C4C4C]">
                                    {t('To Earn')}
                                </p>

                                <p className="text-[15px] text-white">
                                    {formatDisplay(item.earnBuyTp, {
                                        decimalToShow: 2,
                                    })}{' '}
                                    {item.tradeCoinName}
                                </p>
                            </div>

                            <div className="flex justify-between items-center ">
                                <p className="text-[15px] text-[#4C4C4C]">
                                    {t('Entry Price')}
                                </p>

                                <p className="text-[15px] text-white">
                                    {formatDisplay(Number(item.open_price), {
                                        decimalToShow: item.decimalToShow,
                                    })}{' '}
                                </p>
                            </div>

                            <div className="flex justify-between items-center ">
                                <p className="text-[15px] text-[#4C4C4C]">
                                    {t('Current Price')}
                                </p>

                                <p className="text-[15px] text-white">
                                    {formatDisplay(Number(item.price), {
                                        decimalToShow: item.decimalToShow,
                                    })}{' '}
                                </p>
                            </div>
                            <div className="flex justify-between items-center ">
                                <p className="text-[15px] text-[#4C4C4C]">
                                    {t('Liq. Price')}
                                </p>

                                <p className="text-[15px] text-white">
                                    {formatDisplay(
                                        Number(item.liquidationPrice),
                                        {
                                            decimalToShow: item.decimalToShow,
                                        }
                                    )}{' '}
                                </p>
                            </div>

                            <FeesCard item={item} />

                            <Loading
                                isLoading={item.isLoading}
                                className="min-h-[124px] mt-3"
                            >
                                <div
                                    className={clsx(
                                        'rounded-2xl px-2 py-3 flex flex-col justify-center items-center mt-3 relative unrealized-box min-h-[124px]',
                                        {
                                            'bg-transparent2': earn >= 0,
                                            'bg-transparentRed2': earn < 0,
                                        }
                                    )}
                                >
                                    <p
                                        className={clsx(
                                            'text-left text-[15px] text-[#666] opacity-40 mb-1',
                                            {
                                                'text-green1': earn >= 0,
                                                'text-red1': earn < 0,
                                            }
                                        )}
                                    >
                                        {'PNL'}: {percentage(pnl)}
                                    </p>

                                    <p
                                        className={clsx(
                                            'uppercase  leading-4 text-left flex items-center text-2xl mt-2 justify-start ',
                                            {
                                                'text-green1': earn >= 0,
                                                'text-red1': earn < 0,
                                            }
                                        )}
                                    >
                                        {earn > 0 ? '+' : ''}
                                        {formatDisplay(earn, {
                                            decimalToShow: 2,
                                        })}{' '}
                                        {item.tradeCoinName}
                                    </p>

                                    {/* Svg Animate */}
                                    <svg
                                        height="100%"
                                        width="100%"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <rect
                                            stroke={
                                                earn > 0
                                                    ? 'rgba(56, 242, 74, 0.20)'
                                                    : 'rgba(255, 105, 95, 0.20)'
                                            }
                                            rx="8"
                                            ry="8"
                                            className="line"
                                            height="100%"
                                            width="100%"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                            </Loading>
                        </div>
                    </div>
                </Sheet.Content>
            </Sheet.Container>
        </Sheet>
    );
}
